import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { ConfigProvider } from 'antd';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  SquirrelLogo,
} from '@/components';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { getCurrentUser } from './api/auth';
import { clearAuthStorage, getAccessToken, isAccessTokenPersistent, setStoredUserInfo } from './lib/authStorage';
import '@ant-design/v5-patch-for-react-19';

const loginPath = '/login';
const publicPaymentPathPrefix = '/payment-link/';
const operationManualUrl = 'https://wwzxnqwscdk.feishu.cn/wiki/BHMJwms4wigYwqkFL5Wc0WXInMh?from=from_copylink';
const heatingDashboardPath = '/short-video/video-screen/video-screen';
let heatingDashboardWindow: Window | null = null;

function openHeatingDashboard() {
  const markedOpen = window.localStorage.getItem('videoScreenOpen') === '1';
  if (heatingDashboardWindow && !heatingDashboardWindow.closed && markedOpen) {
    heatingDashboardWindow.focus();
    return;
  }

  const opened = window.open(heatingDashboardPath, '_blank');
  if (opened) {
    heatingDashboardWindow = opened;
    window.localStorage.setItem('videoScreenOpen', '1');
  }
}

export function rootContainer(container: React.ReactNode) {
  return <ConfigProvider button={{ autoInsertSpace: false }}>{container}</ConfigProvider>;
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        return undefined;
      }

      const userInfo = await getCurrentUser();
      if (userInfo) {
        setStoredUserInfo(userInfo, isAccessTokenPersistent());
        return {
          name: userInfo.username,
          avatar: '/squirrel-logo.svg',
          userid: userInfo.id?.toString(),
          role: userInfo.role_code || userInfo.role || 'USER',
          access: userInfo.super_admin ? 'SUPER_ADMIN' : (userInfo.role_code || userInfo.role || 'USER'),
          companyId: userInfo.company_id,
          companyName: userInfo.company_name,
          superAdmin: userInfo.super_admin,
          menus: userInfo.menus || [],
        };
      }

      return undefined;
    } catch (error) {
      clearAuthStorage();
      return undefined;
    }
  };

  const { location } = history;
  if (location.pathname.startsWith(publicPaymentPathPrefix)) {
    return { settings: defaultSettings as Partial<LayoutSettings> };
  }
  if (![loginPath].includes(location.pathname)) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
}) => {
  return {
    logo: <SquirrelLogo shape="circle" size={24} />,
    title: '松鼠',
    actionsRender: () => [],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser
          && location.pathname !== loginPath
          && !location.pathname.startsWith(publicPaymentPathPrefix)) {
        history.push(loginPath);
      }
    },
    menuHeaderRender: undefined,
    menuFooterRender: () => <a className="songshu-operation-manual" href={operationManualUrl} target="_blank" rel="noreferrer">操作手册</a>,
    menuItemRender: (item, defaultDom) => {
      const content = defaultDom;

      if (!item.path) {
        return content;
      }

      if (item.path === heatingDashboardPath) {
        return <a href={item.path} target="_blank" rel="noreferrer" onClick={(event) => {
          event.preventDefault();
          openHeatingDashboard();
        }}>{content}</a>;
      }

      if (item.target === '_blank') {
        return <a href={item.path} target="_blank" rel="noreferrer">{content}</a>;
      }

      return <Link to={item.path}>{content}</Link>;
    },
    childrenRender: (children) => children,
    ...initialState?.settings,
  };
};

/**
 * @name request 配置
 * @doc https://umijs.org/docs/max/request
 */
export const request: RequestConfig = {
  baseURL: '/api',
  ...errorConfig,
};
